import React, { useCallback, useEffect, useState } from "react";
import FormField from "../form-field";
import FileUpload from "../form-upload";
import axios from "../../api/httpClient";
import { getApiBaseUrl } from "../../data/authStorage";

const maritalOptions = [
  { value: "", label: "Select status" },
  { value: "single", label: "Single" },
  { value: "married", label: "Married" },
];

const genderOptions = [
  { value: "", label: "Select gender" },
  { value: "Male", label: "Male" },
  { value: "Female", label: "Female" },
];

const defaultForm = {
  firstName: "",
  lastName: "",
  personalEmail: "",
  officialEmail: "",
  phone: "",
  maritalStatus: "",
  gender: "",
  dob: "",
  employeeId: "",
  residentialAddress: "",
  lga: "",
  stateOrigin: "",
  stateResidence: "",
};

export default function PersonalInformationPage({
  data,
  onDataChange,
  photoFile,
  onPhotoChange,
  errors = {},
  showErrors = false,
  generatedEmployeeId = "",
}) {
  const apiBaseUrl = getApiBaseUrl();
  const [form, setForm] = useState({ ...defaultForm, ...(data || {}) });
  const [photoPreviewUrl, setPhotoPreviewUrl] = useState("");
  const [states, setStates] = useState([]);
  const [lgas, setLgas] = useState([]);
  const [locationsLoading, setLocationsLoading] = useState(false);
  const [locationError, setLocationError] = useState("");

  useEffect(() => {
    onDataChange?.(form);
  }, [form, onDataChange]);

  useEffect(() => {
    if (!photoFile) {
      setPhotoPreviewUrl("");
      return;
    }

    const nextUrl = URL.createObjectURL(photoFile);
    setPhotoPreviewUrl(nextUrl);

    return () => {
      URL.revokeObjectURL(nextUrl);
    };
  }, [photoFile]);

  useEffect(() => {
    const loadStates = async () => {
      if (!apiBaseUrl) {
        setLocationError("API base URL is not configured.");
        return;
      }

      try {
        setLocationsLoading(true);
        setLocationError("");
        const response = await axios.get(`${apiBaseUrl}/organizations/locations/nigeria/states`);
        setStates(response?.data?.states || []);
      } catch (requestError) {
        const message = requestError?.response?.data?.message || "Unable to load states.";
        setLocationError(Array.isArray(message) ? message.join(", ") : message);
      } finally {
        setLocationsLoading(false);
      }
    };

    loadStates();
  }, [apiBaseUrl]);

  useEffect(() => {
    const selectedState = form.stateResidence || form.stateOrigin;

    if (!selectedState || !apiBaseUrl) {
      setLgas([]);
      return;
    }

    const loadLgas = async () => {
      try {
        setLocationError("");
        const encodedState = encodeURIComponent(selectedState);
        const response = await axios.get(
          `${apiBaseUrl}/organizations/locations/nigeria/states/${encodedState}/lgas`,
        );
        setLgas(response?.data?.lgas || []);
      } catch (requestError) {
        const message = requestError?.response?.data?.message || "Unable to load LGAs for selected state.";
        setLocationError(Array.isArray(message) ? message.join(", ") : message);
        setLgas([]);
      }
    };

    loadLgas();
  }, [apiBaseUrl, form.stateOrigin, form.stateResidence]);

  const onChange = useCallback((event) => {
    const { name, value } = event.target;

    setForm((prev) => {
      if (name === "stateOrigin" || name === "stateResidence") {
        return { ...prev, [name]: value, lga: "" };
      }

      return { ...prev, [name]: value };
    });
  }, []);

  const handlePhoto = (file) => {
    onPhotoChange?.(file || null);
  };

  const stateOptions = [
    { value: "", label: locationsLoading ? "Loading states..." : "Select state" },
    ...states.map((state) => ({ value: state, label: state })),
  ];

  const lgaOptions = [
    { value: "", label: "Select local government area" },
    ...lgas.map((lga) => ({ value: lga, label: lga })),
  ];

  return (
    <div className="p-6 w-full">
      <h1 className="text-2xl font-semibold mb-6">Personal Information</h1>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {locationError ? (
          <p className="md:col-span-3 text-sm text-red-600" role="alert">
            {locationError}
          </p>
        ) : null}

        <div className="md:col-span-2 space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField id="firstName" name="firstName" label="First name" required placeholder="Enter first name" value={form.firstName} onChange={onChange} error={showErrors ? errors.firstName : ""} />
            <FormField id="lastName" name="lastName" label="Last name" required placeholder="Enter last name" value={form.lastName} onChange={onChange} error={showErrors ? errors.lastName : ""} />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField id="personalEmail" name="personalEmail" label="Personal email address" required placeholder="Enter email address" value={form.personalEmail} onChange={onChange} type="email" error={showErrors ? errors.personalEmail : ""} />
            <FormField id="officialEmail" name="officialEmail" label="Official Email" required placeholder="Official email" value={form.officialEmail} onChange={onChange} type="email" error={showErrors ? errors.officialEmail : ""} />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField id="phone" name="phone" label="Phone number" required placeholder="Enter phone number" value={form.phone} onChange={onChange} type="tel" error={showErrors ? errors.phone : ""} />
            <FormField id="gender" name="gender" label="Gender" required as="select" options={genderOptions} value={form.gender} onChange={onChange} error={showErrors ? errors.gender : ""} />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField id="maritalStatus" name="maritalStatus" label="Marital Status" required as="select" options={maritalOptions} value={form.maritalStatus} onChange={onChange} error={showErrors ? errors.maritalStatus : ""} />
            <FormField id="dob" name="dob" label="Date of birth" required value={form.dob} onChange={onChange} type="date" error={showErrors ? errors.dob : ""} />
          </div>

          <div className="grid grid-cols-1 gap-4">
            <FormField id="residentialAddress" name="residentialAddress" label="Residential Address" required as="textarea" rows={3} placeholder="Enter residential address" value={form.residentialAddress} onChange={onChange} error={showErrors ? errors.residentialAddress : ""} />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-center">
            <FormField
              id="employeeId"
              name="employeeId"
              label="Employee ID"
              value={generatedEmployeeId || ""}
              onChange={() => {}}
              placeholder="Auto-generated after onboarding"
              readOnly
              className="bg-gray-50"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField id="stateOrigin" name="stateOrigin" label="State of Origin" required as="select" options={stateOptions} value={form.stateOrigin} onChange={onChange} error={showErrors ? errors.stateOrigin : ""} />
            <FormField id="stateResidence" name="stateResidence" label="State of Residence" required as="select" options={stateOptions} value={form.stateResidence} onChange={onChange} error={showErrors ? errors.stateResidence : ""} />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField id="lga" name="lga" label="Local Government Area" required as="select" options={lgaOptions} value={form.lga} onChange={onChange} error={showErrors ? errors.lga : ""} />
            <div aria-hidden="true" />
          </div>
        </div>

        <aside className="md:col-span-1">
          <div className="sticky top-6">
            <div className="rounded-lg border border-gray-200 p-6 bg-white">
              <FileUpload onFileChange={handlePhoto} />
            </div>

            {photoFile ? (
              <div className="mt-4 p-4 bg-gray-50 rounded">
                <p className="text-sm font-medium">Ready to upload</p>
                <p className="text-xs text-gray-500 mt-1">{photoFile.name}</p>
                {photoPreviewUrl ? (
                  <img src={photoPreviewUrl} alt="Profile preview" className="mt-3 h-32 w-32 rounded-lg object-cover border border-gray-200" />
                ) : null}
              </div>
            ) : null}
          </div>
        </aside>
      </div>
    </div>
  );
}
